import { useEffect, useState } from 'react'
import { getCable } from '@/common/services/cable'
import { useAuth } from '@/common/stores/auth'
import { ChannelStatus, UseActionCableProps, UseActionCableReturn } from '@/common/types/cable/actionCable.type'

// Subscribes to one channel over the app's single shared AnyCable connection (see services/cable).
// The connection and its reconnection are owned by the cable; this hook only manages this card's
// subscription lifecycle and surfaces its status. Unsubscribing never tears down the shared
// connection — the other cards keep their subscriptions over it.
export function useActionCable<T = unknown>({
  channel,
  params = {},
  onMessage,
  enabled = true,
}: UseActionCableProps<T>): UseActionCableReturn {
  const token = useAuth((state) => state.accessToken)
  const [status, setStatus] = useState<ChannelStatus>(ChannelStatus.Idle)

  const channelId = JSON.stringify({ channel, ...params })

  useEffect(() => {
    if (!enabled || !token) return

    const cable = getCable()
    if (!cable) return

    let active = true
    const subscription = cable.subscribeTo(channel, params as Record<string, string | number | boolean>)
    setStatus(ChannelStatus.Connecting)

    const offs = [
      subscription.on('connect', () => setStatus(ChannelStatus.Connected)),
      subscription.on('disconnect', () => setStatus(ChannelStatus.Disconnected)),
      subscription.on('close', () => setStatus(ChannelStatus.Disconnected)),
      subscription.on('message', (message) => onMessage?.(message as T)),
    ]

    // A refused subscription rejects ensureSubscribed with a SubscriptionRejectedError.
    subscription.ensureSubscribed().catch((error: unknown) => {
      if (active && (error as { name?: string } | null)?.name === 'SubscriptionRejectedError') {
        setStatus(ChannelStatus.Rejected)
      }
    })

    return () => {
      active = false
      offs.forEach((off) => off())
      subscription.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, token, enabled])

  return { status }
}
